import { sessionDisplayTitle, type HermesAPI, type SessionSummary } from "@timibot/core";
import { Observable } from "./observable";

/** 侧边栏里的历史对话列表（按当前 Bot 过滤）。 */
export class SessionListStore extends Observable {
  sessions: SessionSummary[] = [];
  isLoading = false;
  lastError?: string;
  searchText = "";
  private refreshTimer?: ReturnType<typeof setTimeout>;

  setSearch(text: string): void {
    this.searchText = text;
    this.changedNow();
  }

  get filtered(): SessionSummary[] {
    const q = this.searchText.trim().toLowerCase();
    if (!q) return this.sessions;
    return this.sessions.filter((x) => sessionDisplayTitle(x).toLowerCase().includes(q) || (x.preview ?? "").toLowerCase().includes(q));
  }

  /** 按“今天 / 昨天 / 7 天内 / 更早”分组 */
  get grouped(): { title: string; items: SessionSummary[] }[] {
    const buckets: [string, SessionSummary[]][] = [["今天", []], ["昨天", []], ["7 天内", []], ["更早", []]];
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    const day = 86400000;
    for (const x of this.filtered) {
      if (x.started_at === undefined) { buckets[3][1].push(x); continue; }
      const t = x.started_at * 1000;
      if (t >= startOfToday) buckets[0][1].push(x);
      else if (t >= startOfToday - day) buckets[1][1].push(x);
      else if (now.getTime() - t < 7 * day) buckets[2][1].push(x);
      else buckets[3][1].push(x);
    }
    return buckets.filter(([, items]) => items.length).map(([title, items]) => ({ title, items }));
  }

  async refresh(api: HermesAPI | undefined, profile: string | undefined): Promise<void> {
    if (!api) return;
    this.isLoading = true;
    this.changed();
    try {
      // 定时任务的每次运行都是一个会话，数量很多；它们在“工作台 → 自动化”里看，不进对话列表
      this.sessions = (await api.listSessions(profile, 200)).filter((x) => x.source !== "cron");
      this.lastError = undefined;
    } catch (e) {
      this.lastError = e instanceof Error ? e.message : String(e);
    } finally {
      this.isLoading = false;
      this.changed();
    }
  }

  /** sessions.changed 事件可能成串到达，合并成一次刷新 */
  scheduleRefresh(api: HermesAPI | undefined, profile: string | undefined): void {
    clearTimeout(this.refreshTimer);
    this.refreshTimer = setTimeout(() => void this.refresh(api, profile), 600);
  }

  rename(storedID: string, title: string): void {
    const i = this.sessions.findIndex((x) => x.id === storedID);
    if (i < 0) return;
    this.sessions = this.sessions.map((x, j) => (j === i ? { ...x, title } : x));
    this.changed();
  }

  remove(storedID: string): void {
    this.sessions = this.sessions.filter((x) => x.id !== storedID);
    this.changed();
  }

  restore(summary: SessionSummary, index: number): void {
    if (this.sessions.some((x) => x.id === summary.id)) return;
    const s = [...this.sessions];
    s.splice(Math.min(Math.max(index, 0), s.length), 0, summary);
    this.sessions = s;
    this.changed();
  }

  clear(): void {
    this.sessions = [];
    this.changed();
  }
}
